"use client";

import SectionLayout from "@/components/ui/SectionLayout";
import React, { useState } from "react";
import { CaseSectionProps } from "../model";
import { getDefaultCmsSection } from "@/utils/helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/sharp-light-svg-icons";
import SomeSharingLinks from "@/components/ui/SomeSharingLinks";
import { usePathname } from "next/navigation";
import PropertyFacts from "@/components/ui/PropertyFacts";
import Link from "next/link";
import More from "@/components/ui/More";
import CaseTabsComponent from "@/components/CaseTabsComponent";
import FullFinancialFacts from "@/components/ui/FullFinancialFacts";
import StoreContactDetails from "@/components/ui/StoreContactDetails";
import CaseAgent from "@/components/ui/CaseAgent";
import Card from "@/components/ui/Card";
import RippleCtaButton from "@/components/ui/RippleCtaButton";

const CaseClassicPresentationSection: React.FC<CaseSectionProps> = ({
  caseData,
  cmsData,
  pageData,
  app,
  cmsSection,
  pageCtx,
}) => {
  const [selectedView, setSelectedView] = useState<
    "openhouse" | "documents" | "viewing" | "bid"
  >("viewing");
  const pathname = usePathname();
  const fullUrl = `${pageCtx.baseUrl}${pathname}`;
  const encodedFullUrl = encodeURIComponent(fullUrl);
  const somelinks = ["linkedin", "twitter", "facebook", "mail"];
  return (
    <SectionLayout cmsSection={getDefaultCmsSection(cmsSection)}>
      <div className="flex-col w-full lg:flex-row flex mx-auto gap-6 homepage-text-sm">
        <div className="flex-1 flex flex-col gap-4">
          <div>
            <div className="homepage-text-mdtext-[#a7a9ac]">
              {caseData.displayName}
            </div>
            <div className="text-[#333] homepage-text-lg font-extralight">
              {caseData.marketingTexts?.headline}
            </div>
          </div>
          <div className="flex lg:hidden">
            <More>
              <div
                className="styled-description homepage-text-sm"
                dangerouslySetInnerHTML={{
                  __html: caseData.marketingTexts?.description ?? "",
                }}
              />
            </More>
          </div>
          <div
            className="styled-description hidden lg:block"
            dangerouslySetInnerHTML={{
              __html: caseData.marketingTexts?.description ?? "",
            }}
          ></div>
          {caseData.isActive && (
            <CaseTabsComponent
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              app={app}
              cmsData={cmsData}
              caseData={caseData}
              pageData={pageData}
            />
          )}
        </div>
        <div className="w-full lg:w-96 flex flex-col gap-4">
          <SomeSharingLinks
            somelinks={somelinks}
            encodedFullUrl={encodedFullUrl}
            caseData={caseData}
          />
          {/* Financial facts */}
          {caseData.isActive && (
            <Card className="bg-[#ecf0f1]">
              <FullFinancialFacts
                caseData={caseData}
                showStandardFinancing={false}
              />
            </Card>
          )}

          {/* Property facts */}
          <Card className="bg-[#ecf0f1]">
            <PropertyFacts caseData={caseData} />
          </Card>

          {/* Store contact details */}
          {!pageData.showAgent && <StoreContactDetails caseData={caseData} />}

          {/* Case agent */}
          {caseData.agent && pageData.showAgent && (
            <Card className="bg-[#ecf0f1]">
              <CaseAgent
                contentPadding={true}
                cmsSection={cmsSection}
                caseData={caseData}
              />
            </Card>
          )}

          {/* Bestil vurdering */}
          {pageData.evaluationLink && (
            <Link
              aria-label="evaluationLink"
              className="w-full"
              href={pageData.evaluationLink}
            >
              <RippleCtaButton className="w-full">
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon className="size-4" icon={faCalendarDays} />
                  <span>Bestil vurdering</span>
                </span>
              </RippleCtaButton>
            </Link>
          )}
          {pageData.boligsidenBadgeCode && (
            <div className="border flex items-center justify-center">
              <iframe
                title="boligsiden_badge"
                src={`https://maegler.boligsiden.dk/badge/index/${pageData.boligsidenBadgeCode}`}
              ></iframe>
            </div>
          )}
        </div>
      </div>
    </SectionLayout>
  );
};

export default CaseClassicPresentationSection;
